import CircularLoading from '@/common/components/base/Loading/CircularLoading';
import MainModal from '@/common/components/base/modal/Modal';
import { MainModalProps } from '@/common/components/base/modal/Modal.type';
import Typography from '@/common/components/base/typography/Typography';
import { FILE_DETAIL_LINKS } from '@/modules/FileDetail/FileDetailLinks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Box, Center, Flex } from 'styled-system/jsx';
import { WalletPayoutType } from '../../queries/walletPeriodSummary/walletPeriodSummaryQuery.type';
import { walletSettlementFilesQueryOptions } from '../../queries/walletSettlementFiles/walletSettlementFilesQueryOptions';
import RialAmount from '../cashComponent/RialAmount';
import WalletCountBadge from '../walletCards/WalletCountBadge';
import WalletDetailLink from '../walletCards/WalletDetailLink';

type WalletInvoiceDetailProps = {
  payout: WalletPayoutType;
};

export default function WalletInvoiceDetailModal({
  closeModal,
  open,
  payout,
}: MainModalProps & { payout: WalletPayoutType | null }) {
  return (
    <MainModal
      title="جزئیات واریزی"
      titleLineColor="purple-primary"
      closeModal={closeModal}
      open={open}
    >
      {!!payout && <WalletInvoiceDetail payout={payout} />}
    </MainModal>
  );
}

function WalletInvoiceDetail({ payout }: WalletInvoiceDetailProps) {
  const navigate = useNavigate();

  const { data, isPending, isError } = useQuery(
    walletSettlementFilesQueryOptions({ settlementId: payout.settlement_id }),
  );

  if (isError) return <Typography> بارگیری با مشکل مواجه شده است!</Typography>;

  if (isPending) {
    return (
      <Center flexDirection={'column'} gap={16} width={'100%'}>
        <CircularLoading color="colors.purple-primary" />
      </Center>
    );
  }

  const { period, settlement_detail, files } = data;

  return (
    <Flex flexDirection={'column'} gap={16} mt={-16} pb={8}>
      <Flex gap={8} flexDirection={'column'}>
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          bgColor={'white'}
          borderRadius={8}
          px={10}
          py={8}
          filter={'drop-shadow(0px 4px 4px rgba(191,191,191,0.1))'}
        >
          <Typography fontSize={12} color={'black-1'}>
            مبلغ نهایی
          </Typography>
          <RialAmount
            amount={settlement_detail?.total ?? payout?.amount}
            color={'purple-primary'}
            iconBoxSize={24}
            gap={0}
            textProps={{ fontSize: 14, lineHeight: '32px' }}
          />
        </Flex>
        <Flex alignItems={'center'} justifyContent={'space-between'}>
          <Typography fontSize={12} color={'black-1'}>
            زمان واریز
          </Typography>
          <Typography color={'black'}>{payout?.payment_date}</Typography>
        </Flex>
        <Flex alignItems={'center'} justifyContent={'space-between'} gap={16}>
          <Typography fontSize={12} color={'black-1'} flexShrink={0}>
            توضیحات
          </Typography>
          <Typography color={'black'}>
            {settlement_detail?.description || '-'}
          </Typography>
        </Flex>
      </Flex>
      <Box width={'100%'} height={1} bgColor={'gray-2'} />
      <Flex flexDirection={'column'} gap={16}>
        <Flex alignItems={'center'} justifyContent={'space-between'}>
          <Typography textStyle={'h2'} color={'black-1'}>
            پرونده‌ها
          </Typography>
          <WalletCountBadge count={files?.length ?? 0} />
        </Flex>
        {!!period && (
          <Flex alignItems={'center'} justifyContent={'space-between'}>
            <Typography fontSize={12} color={'black-1'}>
              بازه دوره
            </Typography>
            <Flex alignItems={'center'} gap={4}>
              <Typography color={'black'}>{period?.start_date}</Typography>
              <Typography fontSize={12} color={'black-2'}>
                الی
              </Typography>
              <Typography color={'black'}>{period?.end_date}</Typography>
            </Flex>
          </Flex>
        )}
        {files?.map((file, index) => {
          const fileId = file.file_id ?? file.id;
          const referenceId = file.reference_id ?? file.order_id;
          const rows = [
            {
              label: 'شناسه پرونده',
              value: referenceId?.split('_').reverse().join('_'),
            },
            { label: 'تاریخ ایجاد پرونده', value: file.file_created_at },
            { label: 'تاریخ ارسال به بیمه‌گر', value: file.sent_to_insurer_at },
          ];

          return (
            <Flex
              key={fileId ?? index}
              flexDirection={'column'}
              gap={8}
              bgColor={'white'}
              border={'1px solid {colors.gray-3}'}
              borderRadius={8}
              p={12}
              cursor={fileId ? 'pointer' : 'default'}
              onClick={() => {
                if (fileId) navigate(FILE_DETAIL_LINKS.detail(fileId));
              }}
            >
              <Flex
                height={32}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <RialAmount
                  amount={file.amount}
                  color={'#1c1c1c'}
                  textProps={{ fontSize: 16 }}
                />
                {!!fileId && <WalletDetailLink text="جزئیات" />}
              </Flex>
              <Flex flexDirection={'column'} gap={4}>
                {rows.map((row) => (
                  <Flex key={row.label} justifyContent={'space-between'}>
                    <Typography fontSize={12} color={'black-2'}>
                      {row.label}
                    </Typography>
                    <Typography color={'black'}>{row.value || '-'}</Typography>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
